import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
} from "react";
import { useFaceDetection } from "../hooks/useFaceDetection";
import { useObjectDetection } from "../hooks/useObjectDetection";
import { EXAM_MESSAGES } from "../utils/AssessmentStrings";

type SetupStep = "start" | "camera" | "screen" | "ready_to_start" | "ready";

type ProctoringContextType = {
  setupStep: SetupStep;
  setSetupStep: React.Dispatch<React.SetStateAction<SetupStep>>;

  isCameraStarted: boolean;
  setIsCameraStarted: React.Dispatch<React.SetStateAction<boolean>>;

  isScreenStarted: boolean;
  setIsScreenStarted: React.Dispatch<React.SetStateAction<boolean>>;

  cameraStream: MediaStream | null;
  setCameraStream: React.Dispatch<React.SetStateAction<MediaStream | null>>;

  permissionDenied: boolean;
  screenPermissionDenied: boolean;

  handlePermissionDenied: () => void;
  handleScreenPermissionDenied: () => void;

  startCameraSetup: () => void;
  startScreenSetup: () => void;

  handleScreenStreamStop: () => void;

  // detection
  isFaceDetected: boolean;
  isObjectDetected: boolean;
  isMultiFaceDetected: boolean;
  isPositionWrong: boolean;
  positionWarning: string;
  detectedObjects: string[];
  isProctoringActive: boolean;
  setIsProctoringActive: React.Dispatch<React.SetStateAction<boolean>>;
  isBlurred: boolean;
  setIsBlurred: React.Dispatch<React.SetStateAction<boolean>>;

  hasMalpracticeHistory: boolean;
  setHasMalpracticeHistory: React.Dispatch<React.SetStateAction<boolean>>;
  stopProctoring: () => void;
};

const ProctoringContext = createContext<ProctoringContextType | undefined>(
  undefined,
);

export const AssessmentProctoringProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isProctoringActive, setIsProctoringActive] = useState(false);
  const [setupStep, setSetupStep] = useState<SetupStep>("start");

  const [isCameraStarted, setIsCameraStarted] = useState(false);
  const [isScreenStarted, setIsScreenStarted] = useState(false);

  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);

  const [permissionDenied, setPermissionDenied] = useState(false);
  const [screenPermissionDenied, setScreenPermissionDenied] = useState(false);

  const [isBlurred, setIsBlurred] = useState(false);
  const [hasMalpracticeHistory, setHasMalpracticeHistory] = useState(false);

  const isFinishedRef = useRef(false);
  const setupStepRef = useRef(setupStep);
  setupStepRef.current = setupStep;

  // -----------------------------
  // DETECTION (GLOBAL STREAM)
  // -----------------------------

  const {
    isFaceDetected,
    isMultiFaceDetected,
    isPositionWrong,
    positionWarning,
  } = useFaceDetection(
    cameraStream,
    isProctoringActive && !permissionDenied && !screenPermissionDenied,
  );

  const { isObjectDetected, detectedObjects } = useObjectDetection(
    cameraStream,
    isProctoringActive && !permissionDenied && !screenPermissionDenied,
  );

  // -----------------------------
  // BASIC ACTIONS
  // -----------------------------
  const handlePermissionDenied = useCallback(() => {
    setPermissionDenied(true);
  }, []);

  const handleScreenPermissionDenied = useCallback(() => {
    setScreenPermissionDenied(true);
  }, []);

  const startCameraSetup = () => {
    setIsCameraStarted(true);
    setSetupStep("camera");
  };

  const startScreenSetup = () => {
    setIsScreenStarted(true);
    setScreenPermissionDenied(false);
    setSetupStep("screen");
  };

  // -----------------------------
  // VIOLATIONS
  // -----------------------------
  const recordViolation = useCallback((type: string) => {
    if (setupStepRef.current !== "ready" || isFinishedRef.current) return;

    setHasMalpracticeHistory(true);

    const violations = JSON.parse(
      localStorage.getItem("examViolations") || "[]",
    );

    violations.push({
      timestamp: new Date().toISOString(),
      type,
    });

    localStorage.setItem("examViolations", JSON.stringify(violations));
  }, []);

  const handleScreenStreamStop = useCallback(() => {
    if (isFinishedRef.current) return;

    setSetupStep((prev) => {
      if (prev === "ready") {
        recordViolation("screen_sharing_stopped");
        // alert(EXAM_MESSAGES.AUTO_SUBMIT_SCREEN_STOP);
      } else {
        setScreenPermissionDenied(true);
      }
      return prev;
    });
  }, [recordViolation]);
  const stopProctoring = useCallback(() => {
    // stop camera stream
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
    }

    // clear stream
    setCameraStream(null);

    // disable all detection conditions
    setIsProctoringActive(false);

    // reset setup states
    setIsCameraStarted(false);
    setIsScreenStarted(false);
    setSetupStep("start");

    // optional resets
    setPermissionDenied(false);
    setScreenPermissionDenied(false);
    setIsBlurred(false);
  }, [cameraStream]);
  return (
    <ProctoringContext.Provider
      value={{
        setupStep,
        setSetupStep,

        isCameraStarted,
        setIsCameraStarted,

        isScreenStarted,
        setIsScreenStarted,

        cameraStream,
        setCameraStream,

        permissionDenied,
        screenPermissionDenied,

        handlePermissionDenied,
        handleScreenPermissionDenied,

        startCameraSetup,
        startScreenSetup,
        stopProctoring,
        handleScreenStreamStop,

        // detection
        isFaceDetected,
        isObjectDetected,
        isMultiFaceDetected,
        isPositionWrong,
        positionWarning,
        detectedObjects,

        isBlurred,
        setIsBlurred,
        isProctoringActive,
        setIsProctoringActive,
        hasMalpracticeHistory,
        setHasMalpracticeHistory,
      }}
    >
      {children}
    </ProctoringContext.Provider>
  );
};

export const useProctoring = () => {
  const ctx = useContext(ProctoringContext);
  if (!ctx) {
    throw new Error("useProctoring must be used inside ProctoringProvider");
  }
  return ctx;
};
